import { useEffect, useState, useCallback } from "react";
import { Accessibility, X, Type, Contrast, Underline, Pause, BookOpen, RotateCcw, Plus, Minus } from "lucide-react";

const STORAGE_KEY = "phenix_a11y_prefs";
const DEFAULT_PREFS = {
  fontScale: 1,         // 0.875, 1, 1.125, 1.25, 1.5
  contrast: false,
  underline: false,
  reduceMotion: false,
  dyslexic: false,
};

const SCALES = [0.875, 1, 1.125, 1.25, 1.5];
const SCALE_LABEL = { 0.875: "Petit", 1: "Normal", 1.125: "Grand", 1.25: "Plus grand", 1.5: "Très grand" };

function applyPrefs(p) {
  const root = document.documentElement;
  root.style.setProperty("--a11y-font-scale", String(p.fontScale));
  root.classList.toggle("a11y-contrast", p.contrast);
  root.classList.toggle("a11y-underline-links", p.underline);
  root.classList.toggle("a11y-reduce-motion", p.reduceMotion);
  root.classList.toggle("a11y-dyslexic", p.dyslexic);
}

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? { ...DEFAULT_PREFS, ...JSON.parse(raw) } : DEFAULT_PREFS;
    } catch { return DEFAULT_PREFS; }
  });

  useEffect(() => {
    applyPrefs(prefs);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch {}
  }, [prefs]);

  const set = useCallback((patch) => setPrefs((p) => ({ ...p, ...patch })), []);
  const reset = () => setPrefs(DEFAULT_PREFS);

  const scaleIdx = SCALES.indexOf(prefs.fontScale);
  const incScale = () => set({ fontScale: SCALES[Math.min(SCALES.length - 1, scaleIdx + 1)] });
  const decScale = () => set({ fontScale: SCALES[Math.max(0, scaleIdx - 1)] });

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="a11y-open-btn"
        aria-label="Ouvrir les options d'accessibilité"
        className="fixed bottom-4 left-4 z-[60] w-12 h-12 rounded-full bg-plum-600 text-white grid place-items-center shadow-[0_8px_24px_-6px_rgba(91,33,182,0.5)] hover:bg-plum-700 hover:scale-105 active:scale-95 transition-all"
      >
        <Accessibility size={20} strokeWidth={2.2} />
        <span className="sr-only">Accessibilité</span>
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 bg-plum-900/40 backdrop-blur-sm z-[70] animate-fade-up"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-label="Options d'accessibilité"
            className="fixed bottom-4 left-4 right-4 md:right-auto md:w-[380px] bg-white rounded-3xl shadow-2xl z-[80] p-6 animate-fade-up max-h-[88vh] overflow-y-auto"
            data-testid="a11y-panel"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-2xl bg-plum-100 text-plum-700 grid place-items-center">
                  <Accessibility size={16} strokeWidth={2.2} />
                </div>
                <div>
                  <h2 className="font-display font-bold text-plum-900 text-base leading-tight">Accessibilité</h2>
                  <p className="text-[11px] text-plum-700/60">Vos préférences sont mémorisées.</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Fermer"
                data-testid="a11y-close-btn"
                className="w-9 h-9 rounded-full hover:bg-cream-100 grid place-items-center text-plum-700/70"
              >
                <X size={16} />
              </button>
            </div>

            {/* Font size */}
            <Section icon={Type} label="Taille du texte">
              <div className="flex items-center gap-2">
                <button
                  onClick={decScale}
                  disabled={scaleIdx === 0}
                  aria-label="Diminuer la taille du texte"
                  data-testid="a11y-font-dec"
                  className="w-10 h-10 rounded-full bg-cream-100 hover:bg-plum-100 text-plum-700 grid place-items-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Minus size={14} />
                </button>
                <div className="flex-1 text-center">
                  <div className="font-display font-bold text-plum-900 text-sm" data-testid="a11y-font-label">
                    {SCALE_LABEL[prefs.fontScale]}
                  </div>
                  <div className="flex justify-center gap-1 mt-1.5">
                    {SCALES.map((s) => (
                      <div
                        key={s}
                        className={`h-1 rounded-full transition-all ${
                          prefs.fontScale === s ? "w-6 bg-plum-600" : "w-3 bg-cream-200"
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <button
                  onClick={incScale}
                  disabled={scaleIdx === SCALES.length - 1}
                  aria-label="Augmenter la taille du texte"
                  data-testid="a11y-font-inc"
                  className="w-10 h-10 rounded-full bg-cream-100 hover:bg-plum-100 text-plum-700 grid place-items-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus size={14} />
                </button>
              </div>
            </Section>

            <Toggle
              icon={Contrast}
              label="Contraste élevé"
              hint="Améliore la lisibilité"
              checked={prefs.contrast}
              onChange={(v) => set({ contrast: v })}
              testid="a11y-contrast"
            />
            <Toggle
              icon={Underline}
              label="Souligner les liens"
              hint="Identifie tous les liens"
              checked={prefs.underline}
              onChange={(v) => set({ underline: v })}
              testid="a11y-underline"
            />
            <Toggle
              icon={Pause}
              label="Réduire les animations"
              hint="Désactive les mouvements"
              checked={prefs.reduceMotion}
              onChange={(v) => set({ reduceMotion: v })}
              testid="a11y-motion"
            />
            <Toggle
              icon={BookOpen}
              label="Police adaptée"
              hint="Optimisée pour la dyslexie"
              checked={prefs.dyslexic}
              onChange={(v) => set({ dyslexic: v })}
              testid="a11y-dyslexic"
            />

            <button
              onClick={reset}
              data-testid="a11y-reset"
              className="mt-5 w-full flex items-center justify-center gap-2 text-sm font-semibold text-plum-700/70 hover:text-plum-700 py-2.5 rounded-full hover:bg-cream-50 transition-colors"
            >
              <RotateCcw size={13} />
              Réinitialiser les préférences
            </button>
          </div>
        </>
      )}
    </>
  );
}

function Section({ icon: Icon, label, children }) {
  return (
    <div className="mb-4 p-4 bg-cream-25 rounded-2xl">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} strokeWidth={2.2} className="text-plum-700/70" />
        <span className="text-xs font-bold uppercase tracking-wider text-plum-700/70">{label}</span>
      </div>
      {children}
    </div>
  );
}

function Toggle({ icon: Icon, label, hint, checked, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className="w-full flex items-center gap-3 p-4 bg-cream-25 hover:bg-cream-100 rounded-2xl mb-2 transition-colors text-left"
    >
      <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 transition-colors ${
        checked ? "bg-plum-600 text-white" : "bg-cream-100 text-plum-700/60"
      }`}>
        <Icon size={16} strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-plum-900">{label}</div>
        <div className="text-[11px] text-plum-700/60">{hint}</div>
      </div>
      <div className={`w-10 h-6 rounded-full p-0.5 transition-colors shrink-0 ${
        checked ? "bg-plum-600" : "bg-cream-200"
      }`}>
        <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
          checked ? "translate-x-4" : "translate-x-0"
        }`} />
      </div>
    </button>
  );
}
